import { useEffect, useState, useCallback } from "react";
import api from "../../services/api";

function Section({ title, children, right }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function SaveBtn({ loading, onClick, label = "Save" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="px-6 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition"
    >
      {loading ? "Saving…" : label}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition";

const TABS = [
  { id: "faq", label: "FAQ answers", icon: "💬" },
  { id: "teach", label: "Teach it (unanswered)", icon: "🎓" },
  { id: "settings", label: "Look & behavior", icon: "🎨" },
];

export default function AdminChatbot() {
  const [tab, setTab] = useState("faq");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Wellness guide chatbot</h1>
        <p className="text-sm text-gray-400 mt-1">
          Manage the free FAQ chatbot on your public website — no external AI, no ongoing cost.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-100">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-xl transition ${
              tab === t.id
                ? "bg-white border border-b-0 border-gray-100 text-green-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "faq" && <FaqManager />}
      {tab === "teach" && <TeachInbox />}
      {tab === "settings" && <BotSettings />}
    </div>
  );
}

/* ═══════════════════════ FAQ MANAGER ═══════════════════════ */

function FaqManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // faq object or "new"
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/chatbot/admin/faq")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () =>
    setEditing({ question: "", keywords: [], answer: "", isQuickReply: false, order: items.length });

  const save = async (faq) => {
    setSaving(true);
    try {
      if (faq._id) {
        await api.put(`/chatbot/admin/faq/${faq._id}`, faq);
      } else {
        await api.post("/chatbot/admin/faq", faq);
      }
      setEditing(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this answer? This can't be undone.")) return;
    await api.delete(`/chatbot/admin/faq/${id}`);
    load();
  };

  const toggleActive = async (faq) => {
    await api.put(`/chatbot/admin/faq/${faq._id}`, { ...faq, isActive: !faq.isActive });
    load();
  };

  return (
    <Section
      title={`Questions the bot can answer (${items.length})`}
      right={
        <button
          onClick={startNew}
          className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700"
        >
          + Add answer
        </button>
      }
    >
      {editing && (
        <FaqEditor key={editing._id || "new"} faq={editing} saving={saving} onCancel={() => setEditing(null)} onSave={save} />
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">No answers yet. Click "+ Add answer" to start.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((faq) => (
            <div key={faq._id} className="py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-gray-800 text-sm">{faq.question}</p>
                  {faq.isQuickReply && (
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full">
                      quick reply
                    </span>
                  )}
                  {faq.followUps?.length > 0 && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                      {faq.followUps.length} drill-down option{faq.followUps.length > 1 ? "s" : ""}
                    </span>
                  )}
                  {!faq.isActive && (
                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                      hidden
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{faq.answer}</p>
                {faq.keywords?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    also matches: {faq.keywords.join(", ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setEditing(faq)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleActive(faq)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  {faq.isActive ? "Hide" : "Show"}
                </button>
                <button
                  onClick={() => remove(faq._id)}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function FaqEditor({ faq, saving, onCancel, onSave }) {
  const [question, setQuestion] = useState(faq.question || "");
  const [keywords, setKeywords] = useState((faq.keywords || []).join(", "));
  const [answer, setAnswer] = useState(faq.answer || "");
  const [isQuickReply, setIsQuickReply] = useState(!!faq.isQuickReply);
  const [followUps, setFollowUps] = useState(
    faq.followUps?.length ? faq.followUps.map((f) => ({ ...f })) : []
  );

  const addFollowUp = () => setFollowUps((f) => [...f, { label: "", answer: "" }]);
  const updateFollowUp = (i, key, val) =>
    setFollowUps((f) => f.map((item, idx) => (idx === i ? { ...item, [key]: val } : item)));
  const removeFollowUp = (i) => setFollowUps((f) => f.filter((_, idx) => idx !== i));

  return (
    <div className="border border-green-100 bg-green-50/40 rounded-xl p-4 space-y-3">
      <Field label="Question (shown as the button label)">
        <input
          className={inputCls}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. What is Heramba Ayurveda?"
        />
      </Field>
      <Field label="Answer (shown before any drill-down options below, or alone if none)">
        <textarea
          className={inputCls}
          rows={4}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="The answer the bot will give"
        />
      </Field>
      <Field label="Other words/phrases that should also trigger this answer (comma separated)">
        <input
          className={inputCls}
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="e.g. panchakarma, detox, therapy"
        />
      </Field>

      <div className="border border-amber-100 bg-amber-50/50 rounded-xl p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Drill-down options (optional)</p>
            <p className="text-xs text-gray-400">
              e.g. patient asks "doctors" → bot shows clickable buttons like "Dr. Sharma", "Dr. Rao" — each with its own specific answer.
            </p>
          </div>
          <button
            type="button"
            onClick={addFollowUp}
            className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 shrink-0"
          >
            + Add option
          </button>
        </div>

        {followUps.map((fu, i) => (
          <div key={i} className="flex gap-2 items-start bg-white rounded-lg p-2 border border-amber-100">
            <div className="flex-1 space-y-1.5">
              <input
                className={inputCls}
                placeholder="Button label, e.g. Dr. Sharma"
                value={fu.label}
                onChange={(e) => updateFollowUp(i, "label", e.target.value)}
              />
              <textarea
                className={inputCls}
                rows={2}
                placeholder="Answer for this specific option"
                value={fu.answer}
                onChange={(e) => updateFollowUp(i, "answer", e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => removeFollowUp(i)}
              className="text-xs px-2 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 shrink-0"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={isQuickReply}
          onChange={(e) => setIsQuickReply(e.target.checked)}
        />
        Show as a quick-reply button in the chat widget
      </label>
      <div className="flex gap-2 pt-2">
        <SaveBtn
          loading={saving}
          onClick={() =>
            onSave({
              ...faq,
              question,
              answer,
              keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
              isQuickReply,
              followUps: followUps.filter((f) => f.label.trim() && f.answer.trim()),
            })
          }
          label={faq._id ? "Save changes" : "Add answer"}
        />
        <button
          onClick={onCancel}
          className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-semibold hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════ TEACH INBOX (unanswered) ═══════════════════════ */

function TeachInbox() {
  const [items, setItems] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teaching, setTeaching] = useState(null); // question id being taught
  const [answerDraft, setAnswerDraft] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/chatbot/admin/unanswered"), api.get("/chatbot/admin/faq")])
      .then(([u, f]) => {
        setItems(u.data);
        setFaqs(f.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const teach = async (id) => {
    if (!answerDraft.trim()) return;
    await api.post(`/chatbot/admin/unanswered/${id}/resolve`, { answer: answerDraft.trim() });
    setTeaching(null);
    setAnswerDraft("");
    load();
  };

  const linkToExisting = async (id, faqId) => {
    await api.post(`/chatbot/admin/unanswered/${id}/resolve`, { addAsKeywordTo: faqId });
    load();
  };

  const dismiss = async (id) => {
    await api.delete(`/chatbot/admin/unanswered/${id}`);
    load();
  };

  return (
    <Section title={`Questions visitors asked that the bot couldn't answer (${items.length})`}>
      <p className="text-sm text-gray-500">
        Every time someone asks the bot something outside its knowledge, it shows up here. Answer it once and the bot will know it forever — for free.
      </p>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">Nothing to teach right now — the bot has answered everything visitors have asked.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((q) => (
            <div key={q._id} className="py-3 space-y-2">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-gray-800 text-sm">"{q.question}"</p>
                  <p className="text-xs text-gray-400">asked {q.askedCount} time{q.askedCount > 1 ? "s" : ""}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => { setTeaching(q._id); setAnswerDraft(""); }}
                    className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700"
                  >
                    Teach answer
                  </button>
                  <button
                    onClick={() => dismiss(q._id)}
                    className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                  >
                    Dismiss
                  </button>
                </div>
              </div>

              {teaching === q._id && (
                <div className="bg-green-50/50 border border-green-100 rounded-xl p-3 space-y-2">
                  <textarea
                    className={inputCls}
                    rows={3}
                    placeholder="Type the answer to give from now on..."
                    value={answerDraft}
                    onChange={(e) => setAnswerDraft(e.target.value)}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => teach(q._id)}
                      className="text-xs px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                    >
                      Save as new answer
                    </button>
                    {faqs.length > 0 && (
                      <select
                        onChange={(e) => e.target.value && linkToExisting(q._id, e.target.value)}
                        defaultValue=""
                        className="text-xs border border-gray-200 rounded-lg px-2 py-2"
                      >
                        <option value="">…or link to an existing answer instead</option>
                        {faqs.map((f) => (
                          <option key={f._id} value={f._id}>{f.question}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ═══════════════════════ SETTINGS (look & behavior) ═══════════════════════ */

const FONTS = ["Inter", "Montserrat", "Cormorant Garamond", "Poppins", "Lora"];
const EMOJI_PRESETS = ["🙏", "🌿", "🪷", "🕉️", "🍃", "🧘"];

function PhysicianPreview() {
  return (
    <svg width="48" height="48" viewBox="0 0 100 100" role="img" aria-label="Illustrated Ayurvedic physician">
      <circle cx="50" cy="50" r="48" fill="#F5EDD8" />
      <path d="M 30 62 Q 50 50 70 62 L 72 85 Q 50 92 28 85 Z" fill="#4A7C59" />
      <ellipse cx="50" cy="48" rx="17" ry="19" fill="#E8B084" />
      <path d="M 33 44 Q 30 62 38 70 Q 50 76 62 70 Q 70 62 67 44 Q 63 58 50 60 Q 37 58 33 44 Z" fill="#F2F2F0" />
      <path d="M 30 36 Q 30 20 50 18 Q 70 20 70 36 Q 70 30 50 29 Q 30 30 30 36 Z" fill="#E8922A" />
      <ellipse cx="50" cy="26" rx="21" ry="12" fill="#E8922A" />
      <circle cx="50" cy="22" r="4" fill="#F7C948" />
      <circle cx="43" cy="45" r="1.6" fill="#2C1810" />
      <circle cx="57" cy="45" r="1.6" fill="#2C1810" />
      <path d="M 44 53 Q 50 56 56 53" fill="none" stroke="#B86A10" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function BotSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api
      .get("/chatbot/admin/settings")
      .then((r) => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/chatbot/admin/settings", settings);
      setSettings(r.data);
    } catch {
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const uploadIcon = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("iconType", "upload");
      set("iconUrl", r.data.url);
    } catch {
      alert("Icon upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (loading || !settings) return <p className="text-sm text-gray-400">Loading…</p>;

  const toleranceLabel =
    settings.tolerance <= 0.3 ? "Strict" : settings.tolerance >= 0.6 ? "Loose" : "Balanced";

  return (
    <div className="space-y-6">
      <Section title="Identity">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Bot name">
            <input className={inputCls} value={settings.botName} onChange={(e) => set("botName", e.target.value)} />
          </Field>
          <Field label="Subtitle badge">
            <input className={inputCls} value={settings.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
          </Field>
        </div>
        <Field label="Greeting message">
          <textarea className={inputCls} rows={3} value={settings.greeting} onChange={(e) => set("greeting", e.target.value)} />
        </Field>
      </Section>

      <Section title="Icon">
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => { set("iconType", "cutout"); set("iconUrl", "/assets/heramba-guide-cutout.png"); }}
            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 overflow-hidden transition ${
              settings.iconType === "cutout" ? "border-green-500" : "border-transparent hover:bg-gray-100"
            }`}
            title="Your Heramba Guide character (standing cutout, no circle badge)"
          >
            <img
              src="/assets/heramba-guide-cutout.png"
              alt="Heramba Guide"
              style={{ width: "210%", height: "210%", objectFit: "cover", objectPosition: "50% 8%", marginLeft: "-55%" }}
            />
          </button>
          <button
            onClick={() => set("iconType", "illustrated")}
            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 overflow-hidden transition ${
              settings.iconType === "illustrated" ? "border-green-500" : "border-transparent hover:bg-gray-100"
            }`}
            title="Illustrated Ayurvedic physician (animated)"
          >
            <PhysicianPreview />
          </button>
          {EMOJI_PRESETS.map((em) => (
            <button
              key={em}
              onClick={() => { set("iconType", "emoji"); set("iconEmoji", em); }}
              className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl border-2 transition ${
                settings.iconType === "emoji" && settings.iconEmoji === em
                  ? "border-green-500 bg-green-50"
                  : "border-transparent bg-gray-50 hover:bg-gray-100"
              }`}
            >
              {em}
            </button>
          ))}
          <label
            className={`w-12 h-12 rounded-full flex items-center justify-center text-lg border-2 cursor-pointer overflow-hidden transition ${
              settings.iconType === "upload" && settings.iconUrl !== "/assets/heramba-guide-icon.png"
                ? "border-green-500"
                : "border-transparent bg-gray-50 hover:bg-gray-100"
            }`}
            title="Upload your own icon"
          >
            {settings.iconType === "upload" && settings.iconUrl && settings.iconUrl !== "/assets/heramba-guide-icon.png" ? (
              <img src={settings.iconUrl} alt="Bot icon" className="w-full h-full object-cover" />
            ) : (
              "📤"
            )}
            <input type="file" accept="image/*" hidden onChange={(e) => uploadIcon(e.target.files?.[0])} />
          </label>
        </div>
        {uploading && <p className="text-xs text-gray-400">Uploading…</p>}
        <p className="text-xs text-gray-400">Your Heramba Guide character is selected by default — or pick the illustrated physician, a preset emoji, or upload your own image/logo.</p>
      </Section>

      <Section title="Typography & colors">
        <div className="grid sm:grid-cols-4 gap-4">
          <Field label="Font">
            <select className={inputCls} value={settings.fontFamily} onChange={(e) => set("fontFamily", e.target.value)}>
              {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Header ribbon color">
            <input type="color" className="w-full h-11 rounded-xl border border-gray-200 cursor-pointer" value={settings.headerColor} onChange={(e) => set("headerColor", e.target.value)} />
          </Field>
          <Field label="Primary color">
            <input type="color" className="w-full h-11 rounded-xl border border-gray-200 cursor-pointer" value={settings.primaryColor} onChange={(e) => set("primaryColor", e.target.value)} />
          </Field>
          <Field label="Secondary color">
            <input type="color" className="w-full h-11 rounded-xl border border-gray-200 cursor-pointer" value={settings.secondaryColor} onChange={(e) => set("secondaryColor", e.target.value)} />
          </Field>
        </div>
      </Section>

      <Section title="Answer tolerance">
        <Field label={`How hard should the bot try to match a question? (currently: ${toleranceLabel})`}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={settings.tolerance}
            onChange={(e) => set("tolerance", parseFloat(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Strict — only near-exact matches</span>
            <span>Loose — tries harder, may occasionally guess wrong</span>
          </div>
        </Field>
      </Section>

      <SaveBtn loading={saving} onClick={save} label="Save all settings" />
    </div>
  );
}
